import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Sun, Cloud, CloudSun, CloudRain, CloudDrizzle,
  CloudFog, Snowflake, CloudLightning, MapPin, Droplets, Wind
} from 'lucide-react'
import { fetchWeather, fetchWeatherByCoords } from '../../api'

// Icon mapping: icon key -> { component, color }
const WEATHER_ICONS = {
  'sun':        { icon: Sun,            color: 'text-yellow-400' },
  'sun-cloud':  { icon: CloudSun,       color: 'text-yellow-300' },
  'clouds':     { icon: Cloud,          color: 'text-gray-400' },
  'fog':        { icon: CloudFog,       color: 'text-gray-500' },
  'drizzle':    { icon: CloudDrizzle,   color: 'text-blue-300' },
  'rain':       { icon: CloudRain,      color: 'text-blue-400' },
  'rain-heavy': { icon: CloudRain,      color: 'text-blue-500' },
  'snow':       { icon: Snowflake,      color: 'text-blue-200' },
  'snowflake':  { icon: Snowflake,      color: 'text-cyan-300' },
  'storm':      { icon: CloudLightning, color: 'text-amber-400' },
}

// WMO code mapping for client-side geolocation override
const WMO_DECODE = {
  0: ['Clear sky', 'sun'], 1: ['Mainly clear', 'sun-cloud'],
  2: ['Partly cloudy', 'sun-cloud'], 3: ['Overcast', 'clouds'],
  45: ['Foggy', 'fog'], 48: ['Rime fog', 'fog'],
  51: ['Light drizzle', 'drizzle'], 53: ['Drizzle', 'drizzle'],
  55: ['Dense drizzle', 'drizzle'],
  56: ['Freezing drizzle', 'snowflake'], 57: ['Freezing drizzle', 'snowflake'],
  61: ['Slight rain', 'rain'], 63: ['Moderate rain', 'rain'],
  65: ['Heavy rain', 'rain-heavy'],
  66: ['Freezing rain', 'snowflake'], 67: ['Freezing rain', 'snowflake'],
  71: ['Slight snow', 'snow'], 73: ['Moderate snow', 'snow'],
  75: ['Heavy snow', 'snow'], 77: ['Snow grains', 'snow'],
  80: ['Slight showers', 'rain'], 81: ['Moderate showers', 'rain'],
  82: ['Violent showers', 'rain-heavy'],
  85: ['Snow showers', 'snow'], 86: ['Heavy snow showers', 'snow'],
  95: ['Thunderstorm', 'storm'], 96: ['T-storm + hail', 'storm'],
  99: ['T-storm + heavy hail', 'storm'],
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function parseOpenMeteoToSchema(raw, locationName) {
  const cur = raw.current
  const daily = raw.daily
  const [curDesc, curIcon] = WMO_DECODE[cur.weather_code] || ['Unknown', 'clouds']

  const forecast = []
  for (let i = 1; i < Math.min(4, daily.time.length); i++) {
    const dt = new Date(daily.time[i] + 'T12:00:00')
    const [desc, icon] = WMO_DECODE[daily.weather_code[i]] || ['Unknown', 'clouds']
    forecast.push({
      date: daily.time[i],
      day_name: DAY_NAMES[dt.getDay()],
      high: Math.round(daily.temperature_2m_max[i]),
      low: Math.round(daily.temperature_2m_min[i]),
      description: desc,
      icon: icon,
      precipitation_chance: daily.precipitation_probability_max[i],
    })
  }

  return {
    location: { name: locationName },
    current: {
      temperature: Math.round(cur.temperature_2m),
      feels_like: Math.round(cur.apparent_temperature),
      humidity: cur.relative_humidity_2m,
      wind_speed: Math.round(cur.wind_speed_10m),
      description: curDesc,
      icon: curIcon,
    },
    forecast,
  }
}

function getWeatherIcon(iconKey, size = 'w-4 h-4') {
  const config = WEATHER_ICONS[iconKey] || WEATHER_ICONS['clouds']
  const IconComponent = config.icon
  return <IconComponent className={`${size} ${config.color}`} />
}

// Check if coords are near Chicago (~50 miles)
function isNearChicago(lat, lon) {
  return Math.abs(lat - 41.88) < 0.7 && Math.abs(lon - (-87.63)) < 0.7
}

function useGeolocationWeather() {
  const [geoWeather, setGeoWeather] = useState(null)
  const [geoStatus, setGeoStatus] = useState('idle')

  useEffect(() => {
    const saved = localStorage.getItem('weather_geo_pref')
    if (saved === 'denied' || saved === 'chicago') {
      setGeoStatus('done')
      return
    }

    // Re-fetch from saved custom coords
    if (saved === 'custom') {
      const coords = JSON.parse(localStorage.getItem('weather_geo_coords') || '{}')
      if (coords.lat && coords.lon) {
        fetchWeatherByCoords(coords.lat, coords.lon)
          .then(raw => {
            setGeoWeather(parseOpenMeteoToSchema(raw, coords.name || 'Your Location'))
          })
          .catch(() => {})
      }
      setGeoStatus('done')
      return
    }

    // First visit: request geolocation
    if (!navigator.geolocation) {
      setGeoStatus('done')
      return
    }

    setGeoStatus('loading')
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords

        if (isNearChicago(latitude, longitude)) {
          localStorage.setItem('weather_geo_pref', 'chicago')
          setGeoStatus('done')
          return
        }

        try {
          const raw = await fetchWeatherByCoords(latitude, longitude)
          const tz = raw.timezone || 'Unknown'
          const cityName = tz.split('/').pop().replace(/_/g, ' ')
          const parsed = parseOpenMeteoToSchema(raw, cityName)
          setGeoWeather(parsed)
          localStorage.setItem('weather_geo_pref', 'custom')
          localStorage.setItem('weather_geo_coords', JSON.stringify({
            lat: latitude, lon: longitude, name: cityName
          }))
        } catch (err) {
          console.warn('Geolocation weather fetch failed:', err)
        }
        setGeoStatus('done')
      },
      () => {
        localStorage.setItem('weather_geo_pref', 'denied')
        setGeoStatus('done')
      },
      { timeout: 5000, maximumAge: 3600000 }
    )
  }, [])

  return { geoWeather, geoStatus }
}

export default function WeatherTicker({ autoRefresh }) {
  const { data: defaultData } = useQuery({
    queryKey: ['weather'],
    queryFn: fetchWeather,
    refetchInterval: autoRefresh ? 5 * 60 * 1000 : false,
    retry: 1,
  })

  const { geoWeather } = useGeolocationWeather()

  const weather = geoWeather || defaultData
  if (!weather) return null

  const { current, forecast, location } = weather

  return (
    <div className="relative overflow-hidden bg-gray-900/60 border-y border-cyan-500/10 py-2 md:py-2.5 mb-4 md:mb-6 -mx-3 md:mx-0 md:rounded-lg animate-fade-in">
      <div className="flex items-center justify-between px-3 md:px-4 gap-3">

        {/* Current conditions */}
        <div className="flex items-center gap-2.5 md:gap-3 flex-shrink-0">
          {getWeatherIcon(current.icon, 'w-5 h-5 md:w-6 md:h-6')}
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg md:text-xl font-black text-white leading-none">
              {current.temperature}°
            </span>
            <span className="text-[10px] md:text-xs text-gray-500 font-medium hidden sm:inline">
              {current.description}
            </span>
          </div>
          <span className="w-px h-4 bg-white/10 mx-1 hidden sm:block" />
          <div className="hidden sm:flex items-center gap-2 text-[10px] text-gray-500">
            <span className="flex items-center gap-0.5">
              <Droplets className="w-2.5 h-2.5" />
              {current.humidity}%
            </span>
            <span className="flex items-center gap-0.5">
              <Wind className="w-2.5 h-2.5" />
              {current.wind_speed} mph
            </span>
          </div>
          <span className="w-px h-4 bg-white/10 mx-1" />
          <span className="flex items-center gap-0.5 text-[9px] md:text-[10px] text-gray-600">
            <MapPin className="w-2.5 h-2.5" />
            {location.name}
          </span>
        </div>

        {/* 3-day forecast */}
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          {forecast.map((day) => (
            <div key={day.date} className="flex flex-col items-center gap-0.5 min-w-[36px] md:min-w-[44px]">
              <span className="text-[9px] md:text-[10px] font-semibold text-gray-500 tracking-wide">
                {day.day_name}
              </span>
              {getWeatherIcon(day.icon, 'w-3.5 h-3.5')}
              <div className="flex items-baseline gap-0.5">
                <span className="text-[11px] md:text-xs font-bold text-white">{day.high}°</span>
                <span className="text-[9px] md:text-[10px] text-gray-600">{day.low}°</span>
              </div>
              {day.precipitation_chance > 20 && (
                <span className="text-[8px] text-blue-400 flex items-center gap-0.5">
                  <Droplets className="w-2 h-2" />
                  {day.precipitation_chance}%
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
